import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search } from 'lucide-react';
import { SIDEBAR_ICONS } from './Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';

// Quick-navigation modal (Cmd+K / Ctrl+K). Mirrors the sidebar's own item
// list (via getMenuItems, passed in as `items`) so it never drifts out of
// sync with what tabs actually exist for a given user/plan.
const CommandPalette = ({ isOpen, onClose, items, onSelect }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.name.toLowerCase().includes(q));
  }, [query, items]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHighlighted(0);
      // Autofocus after the modal mounts.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((i) => (filtered.length ? (i + 1) % filtered.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((i) => (filtered.length ? (i - 1 + filtered.length) % filtered.length : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[highlighted];
        if (item) onSelect(item.id);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filtered, highlighted, onClose, onSelect]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 ap-modal-backdrop"
      style={{ zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14vh' }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: '#fff',
          borderRadius: 14,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2), 0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Search className="w-4 h-4" strokeWidth={1.75} style={{ color: '#A3A3A3', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('commandPalette.placeholder', 'Ir a una sección...')}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: 14.5,
              letterSpacing: '-0.01em',
              fontFamily: 'Inter, system-ui, sans-serif',
              color: '#1d1d1f',
              background: 'transparent',
            }}
          />
          <kbd style={{
            fontSize: 11, fontWeight: 500, color: '#A3A3A3', background: '#F5F5F7',
            border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5, padding: '2px 6px', flexShrink: 0,
          }}>
            Esc
          </kbd>
        </div>

        <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: 6 }}>
          {filtered.length === 0 && (
            <div style={{ padding: '24px 12px', textAlign: 'center', fontSize: 13.5, color: '#A3A3A3' }}>
              {t('commandPalette.noResults', 'Sin resultados para "{query}"').replace('{query}', query)}
            </div>
          )}
          {filtered.map((item, i) => {
            const IconComp = SIDEBAR_ICONS[item.icon];
            const isHighlighted = i === highlighted;
            return (
              <button
                key={item.id}
                onClick={() => onSelect(item.id)}
                onMouseEnter={() => setHighlighted(i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 10px',
                  borderRadius: 8,
                  border: 'none',
                  background: isHighlighted ? 'rgba(0,0,0,0.06)' : 'transparent',
                  color: '#1d1d1f',
                  fontSize: 13.5,
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {IconComp && <IconComp className="w-4 h-4" strokeWidth={1.75} style={{ flexShrink: 0, color: '#6e6e73' }} />}
                <span style={{ flex: 1 }}>{item.name}</span>
                {item.group && (
                  <span style={{ fontSize: 11.5, color: '#A3A3A3' }}>{item.group}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
